import type { FC, RefObject } from 'react';
import React, { useEffect, useState, useRef, useImperativeHandle } from 'react';
import cn from 'classnames';
import type {
    TChangeEventHandler,
    TFocusEventHandler,
    TKeyboardEventHandler,
} from '@alfa-bank/site-pure-input/src/models';

import { DEFAULT_FIELDS_LENGTH, KEYS } from './configs';
import type { TProps, TType } from './models';

interface IState {
    values: string[];
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const CodeInput: FC<TProps> = ({
    name = '',
    dataTestId = 'codeInput',
    type = 'number' as TType,
    fields = DEFAULT_FIELDS_LENGTH,
    disabled = false,
    required = false,
    styles,
    Aligner,
    Input,
    Text,
    className,
    error,
    captionAlign,
    autoFocus = false,
    successMessage,
    onRef,
    ...restProps
}) => {
    const iRefs: Array<RefObject<HTMLInputElement>> = Array.from(
        Array(fields).keys(),
        // eslint-disable-next-line react-hooks/rules-of-hooks
        () => useRef(null)
    );

    const initValues = () => {
        if (restProps.values && restProps.values.length) {
            return restProps.values.reduce(
                (acc: string[], v) => (acc.length <= fields ? [...acc, v] : acc),
                []
            );
        }

        return Array(fields).fill('');
    };

    const [values, setState] = useState<IState['values']>(initValues());

    /**
     * Фокусируется на первом поле
     */
    const focusOnFirstField = () => {
        iRefs[0].current?.focus();
    };

    /**
     * Очищает поля и фокусируется на первом поле
     */
    const reset = () => {
        setState(Array(fields).fill(''));

        focusOnFirstField();
    };

    /**
     * Фокусируется на первом поле при рендере
     */
    useEffect(() => {
        if (autoFocus) {
            focusOnFirstField();
        }
    }, []);

    useImperativeHandle(onRef, () => ({
        reset,
    }));

    const triggerChange = (argumentValues: string[]) => {
        const valuesToProcess = argumentValues || values;
        const val = valuesToProcess.join('');
        if (restProps.onChange) {
            restProps.onChange(val);
        }

        if (restProps.onComplete && val.length >= fields) {
            restProps.onComplete(val);
        }
    };

    const onChange: TChangeEventHandler = ({ event, value }) => {
        let newValue = String(value);

        if (!fields) return;

        const { target } = event;
        const index = parseInt(target.dataset.id!, 10);

        if (type === 'number') {
            newValue = target.value.replace(/\D/g, '');
        }

        if (value === '' || (type === 'number' && !target.validity.valid)) {
            return;
        }

        let nextRef;

        const newArrayOfValues: IState['values'] = Object.assign([], values);

        if (newValue.length > 1) {
            let nextIndex = newValue.length + index - 1;

            if (nextIndex >= fields) {
                nextIndex = fields - 1;
            }
            nextRef = iRefs[nextIndex];

            newValue.split('').forEach((item, i) => {
                const cursor = index + i;

                if (cursor < fields) {
                    newArrayOfValues[cursor] = item;
                }
            });
        } else {
            nextRef = iRefs[index + 1];
            newArrayOfValues[index] = newValue;
        }
        setState(newArrayOfValues);
        if (nextRef) {
            nextRef.current?.focus();
            nextRef.current?.select();
        }

        triggerChange(newArrayOfValues);
    };

    const onKeyDown: TKeyboardEventHandler = ({ event }) => {
        const target = event.target as HTMLInputElement;
        const index = parseInt(target.dataset.id!, 10);
        const prevIndex = index - 1;
        const nextIndex = index + 1;
        const prevRef = iRefs[prevIndex];
        const nextRef = iRefs[nextIndex];
        const vals = Array.from(values);

        switch (event.key) {
            case KEYS.backspace:
                event.preventDefault();

                if (values[index]) {
                    vals[index] = '';
                } else if (prevRef) {
                    vals[prevIndex] = '';
                    prevRef.current?.focus();
                }
                setState(vals);
                triggerChange(vals);
                break;

            case KEYS.left:
                event.preventDefault();
                if (prevRef) {
                    prevRef.current?.focus();
                }
                break;

            case KEYS.right:
                event.preventDefault();
                if (nextRef) {
                    nextRef.current?.focus();
                }
                break;

            case KEYS.up:
            case KEYS.down:
                event.preventDefault();
                break;

            default:
                break;
        }
    };

    const onFocus: TFocusEventHandler = ({ event }) => {
        event.target.select();
    };

    const pattern = type === 'number' ? '[0-9]*' : undefined;
    const hasStatus = Boolean(error || successMessage);

    return (
        <div className={cn(styles.container, className)} data-test-id={dataTestId}>
            {values.map((value, index) => (
                <Input
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    className={cn(styles.external, styles.input, {
                        [styles.hasStatus]: hasStatus,
                    })}
                    data-id={index}
                    dataTestId={`input-${index}`}
                    disabled={disabled}
                    error={!!error}
                    forwardedRef={iRefs[index]}
                    name={name}
                    onChange={onChange}
                    onFocus={onFocus}
                    onKeyDown={onKeyDown}
                    pattern={pattern}
                    placeholder="&#9679;"
                    required={required}
                    type={type === 'number' ? 'tel' : type}
                    valid={!!successMessage}
                    value={value}
                />
            ))}

            {hasStatus && (
                <Aligner align={captionAlign}>
                    <Text
                        className={cn(styles.external, styles.caption, {
                            [styles.errorMessage]: error,
                            [styles.successMessage]: successMessage,
                        })}
                        size="m"
                    >
                        {error || successMessage}
                    </Text>
                </Aligner>
            )}
        </div>
    );
};
